import { useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { SectionCard, CompanyProfileIcon } from '@platform/ui-kit';
import { HelpPanel, type HelpContent } from '../portfolio_definition/HelpPanel';
import { api, type CompanyProfile } from '../../api';
import { describeError, useAsyncData } from './useAsyncData';

/**
 * Screen 1 — Company profile (spec §A).
 *
 * One install is one client company, so this is a single form. It is grouped into
 * fieldsets rather than one long column, and every row pairs fields of equal height
 * — no helper text on one field of a pair, which would knock the two out of
 * alignment.
 */

/** Two-column field row that collapses to one column on small screens. */
function FieldRow({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
        alignItems: 'start',
      }}
    >
      {children}
    </Box>
  );
}

const SIC_PATTERN = /^\d{2,4}$/;

/**
 * SIC code editor.
 *
 * Order carries meaning — the first code is the primary classification, which is
 * what downstream analysis keys on when it needs a single value. So the list is
 * explicitly re-orderable rather than a plain set.
 */
function SicCodeEditor({
  codes,
  onChange,
}: {
  codes: string[];
  onChange: (next: string[]) => void;
}) {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string>();

  const add = () => {
    const code = draft.trim();
    if (!code) return;
    if (!SIC_PATTERN.test(code)) {
      setError('A SIC code is 2 to 4 digits, e.g. 3711.');
      return;
    }
    if (codes.includes(code)) {
      setError(`${code} is already listed.`);
      return;
    }
    onChange([...codes, code]);
    setDraft('');
    setError(undefined);
  };

  const remove = (code: string) => onChange(codes.filter((c) => c !== code));
  const makePrimary = (code: string) => onChange([code, ...codes.filter((c) => c !== code)]);

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <TextField
          label="Add SIC code"
          size="small"
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            setError(undefined);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
          error={Boolean(error)}
          helperText={error ?? ' '}
          inputProps={{ inputMode: 'numeric', maxLength: 4, 'aria-label': 'SIC code' }}
          sx={{ width: 200 }}
        />
        <Button variant="outlined" onClick={add} disabled={!draft.trim()} sx={{ height: 40 }}>
          Add
        </Button>
      </Stack>

      {codes.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No classification recorded. Add at least one SIC code.
        </Typography>
      ) : (
        <Box>
          <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
            {codes.map((code, index) => {
              const isPrimary = index === 0;
              return (
                <Tooltip
                  key={code}
                  title={isPrimary ? 'Primary classification' : 'Click to make primary'}
                >
                  <Chip
                    label={isPrimary ? `${code} · Primary` : code}
                    color={isPrimary ? 'primary' : 'default'}
                    variant={isPrimary ? 'filled' : 'outlined'}
                    onClick={isPrimary ? undefined : () => makePrimary(code)}
                    onDelete={() => remove(code)}
                  />
                </Tooltip>
              );
            })}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
            The primary code is used wherever a single classification is needed.
          </Typography>
        </Box>
      )}
    </Stack>
  );
}

/**
 * Two things on this screen are not cosmetic, and the drawer says so: the first SIC code
 * is the primary classification that downstream analysis keys on, and the email domains
 * decide who an arriving SSO user is matched to. Both look like free-text detail.
 */
const COMPANY_PROFILE_HELP: HelpContent = {
  title: 'Company Profile',
  icon: '🏢',
  purpose:
    'Records <strong>who this install belongs to</strong>. One install is one client company, so this is a single form rather than a list &mdash; and several fields here are read by the rest of the platform rather than just filed away.',
  doHere: [
    'Set the <strong>company name</strong>, which appears in the portal&rsquo;s own app bar, and the legal name if it differs.',
    'Record the <strong>contact</strong> details and the <strong>corporate address</strong>.',
    'Add one or more <strong>SIC codes</strong>, and choose which is primary.',
    'Set the <strong>locale</strong> and <strong>timezone</strong> for this install.',
    'List the <strong>email domains</strong> that identify this company to single sign-on.',
    'Save with <strong>Save changes</strong>, or abandon an edit with <strong>Discard</strong> &mdash; both sit in the bar pinned to the foot of the form.',
  ],
  fits: 'Administration &rarr; Company Profile',
  firstTime:
    'Fill in the company name first &mdash; it is the one required field, and it is the name every user sees in the app bar. The rest can be completed at any time.',
  sections: [
    {
      type: 'html',
      label: 'The first SIC code is the primary one',
      html: 'Order carries meaning here. A company can be classified under several SIC codes, but wherever the platform needs <strong>a single classification</strong> it takes the first one, shown chipped as <em>Primary</em>. Click any other code to promote it. This is why the list is explicitly re-orderable rather than an unordered set &mdash; re-ordering changes what downstream analysis reads.',
    },
    {
      type: 'html',
      label: 'Email domains are matching rules, not contact addresses',
      html: 'The domains under <strong>Single sign-on matching</strong> are how a person arriving from an identity provider is recognised as belonging to this company. Someone signing in as <code>name@meridian.com</code> is matched by the domain <code>meridian.com</code> appearing in this list. They are not an address anything is sent to, and <strong>getting them wrong is an access problem, not a cosmetic one</strong>: a missing domain can leave a legitimate user unmatched, and a domain that does not belong to the client should never be listed.',
    },
    {
      type: 'defs',
      label: 'Fields other screens read',
      items: [
        {
          term: 'Company name',
          def: 'Shown in the portal app bar to every user of this install. Required.',
        },
        {
          term: 'Logo URL',
          def: 'Where the client&rsquo;s mark is fetched from for portal branding. A link to an image, not an upload.',
        },
        {
          term: 'Email domains',
          def: 'Used for SSO matching, as above. The SSO screen can override them per deployment; left blank there, it falls back to these.',
        },
        {
          term: 'Locale and timezone',
          def: 'The install&rsquo;s defaults for formatting dates and numbers. Individual screens still show timestamps in each viewer&rsquo;s own timezone where that is more useful.',
        },
      ],
    },
    {
      type: 'html',
      label: 'Saving is explicit',
      html: 'Nothing is written as you type. The bar at the foot tells you whether there are <strong>unsaved changes</strong> and when the profile was <strong>last saved</strong>; it stays in view while you work down a long form, so the state of the form is never off screen. <strong>Discard</strong> returns every field to the last saved values.',
    },
  ],
};

export function CompanyProfileTab() {
  const { data, loading, error, reload } = useAsyncData<CompanyProfile>(() =>
    api.admin.getCompany(),
  );
  const [form, setForm] = useState<CompanyProfile>();
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string>();
  const [saved, setSaved] = useState(false);

  useEffect(() => setForm(data), [data]);

  const dirty = useMemo(
    () => Boolean(form && data && JSON.stringify(form) !== JSON.stringify(data)),
    [form, data],
  );

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!form) return null;

  const set = <K extends keyof CompanyProfile>(key: K, value: CompanyProfile[K]) =>
    setForm({ ...form, [key]: value });

  const text = (key: keyof CompanyProfile, label: string) => (
    <TextField
      label={label}
      value={(form[key] as string | null) ?? ''}
      onChange={(e) => set(key, (e.target.value || null) as CompanyProfile[typeof key])}
      fullWidth
    />
  );

  const save = async () => {
    setSaving(true);
    setSaveError(undefined);
    try {
      await api.admin.updateCompany({
        companyName: form.company_name,
        legalName: form.legal_name,
        logoUrl: form.logo_url,
        emailDomains: form.email_domains,
        locale: form.locale,
        timezone: form.timezone,
        supportContact: form.support_contact,
        phone: form.phone,
        addressLine1: form.address_line1,
        addressLine2: form.address_line2,
        city: form.city,
        stateRegion: form.state_region,
        postalCode: form.postal_code,
        country: form.country,
        sicCodes: form.sic_codes,
      });
      setSaved(true);
      reload();
    } catch (err) {
      setSaveError(describeError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 880 }}>
      <HelpPanel help={COMPANY_PROFILE_HELP} />

      <Stack spacing={2.5}>
        {saveError ? (
          <Alert severity="error" onClose={() => setSaveError(undefined)}>
            {saveError}
          </Alert>
        ) : null}

        <SectionCard
          title={
            <Stack direction="row" spacing={1} alignItems="center">
              <CompanyProfileIcon fontSize="small" />
              <span>Identity</span>
            </Stack>
          }
        >
          <Stack spacing={2}>
            <FieldRow>
              <TextField
                label="Company name"
                value={form.company_name}
                onChange={(e) => set('company_name', e.target.value)}
                required
                error={!form.company_name.trim()}
                fullWidth
              />
              {text('legal_name', 'Legal name')}
            </FieldRow>
            {text('logo_url', 'Logo URL')}
            <Typography variant="caption" color="text.secondary">
              The company name appears in the portal app bar.
            </Typography>
          </Stack>
        </SectionCard>

        <SectionCard title="Contact">
          <Stack spacing={2}>
            <FieldRow>
              {text('phone', 'Phone')}
              {text('support_contact', 'Support contact')}
            </FieldRow>
          </Stack>
        </SectionCard>

        <SectionCard title="Corporate address">
          <Stack spacing={2}>
            {text('address_line1', 'Address line 1')}
            {text('address_line2', 'Address line 2')}
            <FieldRow>
              {text('city', 'City')}
              {text('state_region', 'State / Region')}
            </FieldRow>
            <FieldRow>
              {text('postal_code', 'Postal code')}
              {text('country', 'Country')}
            </FieldRow>
          </Stack>
        </SectionCard>

        <SectionCard
          title="Industry classification"
          subheader="SIC codes for this company — one or many."
        >
          <SicCodeEditor codes={form.sic_codes} onChange={(next) => set('sic_codes', next)} />
        </SectionCard>

        <SectionCard title="Regional settings">
          <FieldRow>
            <TextField
              label="Locale"
              value={form.locale}
              onChange={(e) => set('locale', e.target.value)}
              fullWidth
            />
            <TextField
              label="Timezone"
              value={form.timezone}
              onChange={(e) => set('timezone', e.target.value)}
              fullWidth
            />
          </FieldRow>
        </SectionCard>

        <SectionCard
          title="Single sign-on matching"
          subheader="Users arriving from the identity provider with these email domains map to this company."
        >
          <TextField
            label="Email domains"
            value={form.email_domains.join(', ')}
            onChange={(e) =>
              set(
                'email_domains',
                e.target.value
                  .split(',')
                  .map((d) => d.trim())
                  .filter(Boolean),
              )
            }
            fullWidth
            placeholder="meridian.com, meridian.co.uk"
          />
        </SectionCard>
      </Stack>

      {/* Sticky action bar: the form is long enough that a footer button would sit
          below the fold while the user is editing the top of it. */}
      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          mt: 3,
          py: 2,
          px: 2.5,
          bgcolor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
          borderRadius: 1,
          boxShadow: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          {dirty
            ? 'You have unsaved changes.'
            : data?.updated_at
              ? `Last saved ${new Date(data.updated_at).toLocaleString()}`
              : 'No changes yet.'}
        </Typography>
        <Button onClick={() => setForm(data)} disabled={!dirty || saving}>
          Discard
        </Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={!dirty || saving || !form.company_name.trim()}
        >
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </Box>

      <Snackbar
        open={saved}
        autoHideDuration={4000}
        onClose={() => setSaved(false)}
        message="Company profile saved"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
